const JOUR_LABELS = {
  0: 'Dimanche',
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
};

const timeToMinutes = (value) => {
  const [h, m] = String(value || '0:0').split(':').map(Number);
  return h * 60 + m;
};

/** Pas entre deux RDV = durée prestation (aligné backend resolvePasReservation). */
export const resolvePasReservationMinutes = (commercant, prestationForm) => {
  const duree = parseInt(prestationForm?.dureeMinutes, 10) || parseInt(commercant?.dureeRDVMinutes, 10) || 60;
  if ((prestationForm?.maxParticipants ?? 1) <= 1) return Math.max(15, duree);
  const pasShop = parseInt(commercant?.pasGrilleMinutes, 10);
  return [15, 30].includes(pasShop) ? Math.max(pasShop, duree) : duree;
};

export const countSlotsInPlage = (heureDebut, heureFin, pasMinutes, dureeMinutes) => {
  const debut = timeToMinutes(heureDebut);
  const fin = timeToMinutes(heureFin);
  const duree = parseInt(dureeMinutes, 10) || 60;
  const pas = parseInt(pasMinutes, 10) || duree;
  if (fin <= debut || duree <= 0 || pas <= 0) return 0;
  let count = 0;
  for (let m = debut; m + duree <= fin; m += pas) count += 1;
  return count;
};

export const summarizeCommercantHoraires = (horaires = []) => {
  const byDay = new Map();
  (Array.isArray(horaires) ? horaires : [])
    .filter((h) => h?.actif !== false && h?.heureDebut && h?.heureFin)
    .forEach((h) => {
      const jour = Number(h.jour);
      if (!byDay.has(jour)) byDay.set(jour, []);
      byDay.get(jour).push(`${h.heureDebut} – ${h.heureFin}`);
    });

  return [...byDay.entries()]
    .sort(([a], [b]) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
    .map(([jour, plages]) => ({
      jour,
      label: JOUR_LABELS[jour] || `Jour ${jour}`,
      plages,
    }));
};

export const buildPlagesFromCommercantHoraires = (horaires = []) =>
  (Array.isArray(horaires) ? horaires : [])
    .filter((h) => h?.actif !== false && h?.heureDebut && h?.heureFin)
    .map((h) => ({
      type: 'RECURRENT',
      jourSemaine: Number(h.jour),
      heureDebut: h.heureDebut,
      heureFin: h.heureFin,
      actif: true,
    }))
    .filter((p) => Number.isFinite(p.jourSemaine));

export const formatPlageLabel = (creneau) => {
  const jours = JOUR_LABELS[creneau.jourSemaine] || 'Jour';
  if (creneau.type === 'EXCEPTIONNEL' && creneau.dateSpecifique) {
    return `${creneau.dateSpecifique} · ${creneau.heureDebut} – ${creneau.heureFin}`;
  }
  return `${jours} · ${creneau.heureDebut} – ${creneau.heureFin}`;
};

const JOUR_SHORT = {
  0: 'Dim',
  1: 'Lun',
  2: 'Mar',
  3: 'Mer',
  4: 'Jeu',
  5: 'Ven',
  6: 'Sam',
};

/** Résumé des jours de plages configurées (pas le nombre d'enregistrements). */
export const summarizePrestationPlageDays = (creneaux = []) => {
  const active = (Array.isArray(creneaux) ? creneaux : []).filter((c) => c?.actif !== false);
  if (active.length === 0) return null;

  const recurrentDays = [
    ...new Set(
      active
        .filter((c) => c.type !== 'EXCEPTIONNEL' && Number.isFinite(Number(c.jourSemaine)))
        .map((c) => Number(c.jourSemaine)),
    ),
  ].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b));

  if (recurrentDays.length > 0) {
    return recurrentDays.map((d) => JOUR_SHORT[d] || JOUR_LABELS[d] || `J${d}`).join(', ');
  }

  const exceptionCount = active.filter((c) => c.type === 'EXCEPTIONNEL').length;
  if (exceptionCount > 0) {
    return `${exceptionCount} date${exceptionCount > 1 ? 's' : ''} exceptionnelle${exceptionCount > 1 ? 's' : ''}`;
  }

  return null;
};

/** Libellé badge carte prestation (jours de plages, ou repli explicite). */
export const getPrestationPlageBadge = (prestation) => {
  const days = summarizePrestationPlageDays(prestation?.creneaux);
  if (days) {
    return { label: days, title: `Plages : ${days}`, variant: 'custom' };
  }

  const plageCount = prestation?._count?.creneaux ?? 0;
  if (plageCount > 0) {
    const label = `${plageCount} plage${plageCount > 1 ? 's' : ''}`;
    return {
      label,
      title: `${plageCount} plage(s) personnalisée(s) — ouvrez la prestation pour voir le détail`,
      variant: 'custom',
    };
  }

  return { label: 'Horaires auto', title: 'Disponibilité déduite des horaires boutique', variant: 'auto' };
};
